import React, { useEffect, useState } from "react";
import { Drawer, DrawerRef } from "../../../components/drawers/Drawer";
import Indicator from "../../../components/base/Indicator";
import Icon from "../../../components/base/Icon";
import { useHub } from "../../../core/Global";
import { toastError } from "../../../components/base/Toast";
import { CatalogChange, CatalogPlan } from "../models/PlanCatalog";
import { toDateTime } from "./format";


const kinds = {
    plan: { icon: "pen", text: "Plan edited" },
    prices: { icon: "tags", text: "General prices" },
    apply: { icon: "paper-plane", text: "Sent to subscribers" }
} as const


// Everything ever changed on the catalog: who, when, and each figure from and to.
export default function HistoryDrawer(props: {
    plans: CatalogPlan[],
    onClose: () => void
}) {

    const hub = useHub()
    const drawer = React.useRef<DrawerRef>(null)

    const [code, codeSet] = useState<string>("")
    const [items, itemsSet] = useState<CatalogChange[]>([])
    const [loading, loadingSet] = useState<boolean>(true)

    useEffect(() => {
        loadingSet(true)
        hub.Get("/system/plancatalog/history" + (code ? "?code=" + code : ""), res => {
            loadingSet(false)
            if (res.success) itemsSet(res.data ?? [])
            else toastError(res.message ?? "Couldn't load the history.")
        }, () => loadingSet(false))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [code])

    return (
        <Drawer ref={drawer} id="drawer-plan-history"
            title="Catalog history"
            titleIcon="clock-rotate-left"
            subtitle="Every change, newest first"
            size="medium"
            onClose={props.onClose}>

            <div className="catalog-history-filter">
                <button type="button" className={"catalog-filter" + (code === "" ? " is-active" : "")}
                    onClick={() => codeSet("")}>All</button>
                {
                    props.plans.map(p =>
                        <button key={p.code} type="button"
                            className={"catalog-filter" + (code === p.code ? " is-active" : "")}
                            onClick={() => codeSet(p.code ?? "")}>
                            {p.name}
                        </button>
                    )
                }
            </div>

            {
                loading ?
                    <div className="py-20"><Indicator /></div>
                    : items.length === 0 ?
                        <div className="text-center text-gray-500 py-20">Nothing has been changed yet.</div>
                        :
                        <div className="catalog-history">
                            {
                                items.map(c => {
                                    const kind = kinds[c.kind ?? "plan"] ?? kinds.plan
                                    return (
                                        <div key={c.id} className={"catalog-history-item is-" + c.kind}>
                                            <div className="catalog-history-icon">
                                                <Icon icon={kind.icon as any} type="solid" size={12} />
                                            </div>
                                            <div className="min-w-0 flex-grow-1">
                                                <div className="fw-bold text-gray-900">
                                                    {c.kind === "prices" ? kind.text : `${c.planName} · ${kind.text}`}
                                                </div>
                                                <div className="text-gray-500 fs-8">
                                                    {c.byName} · {toDateTime(c.createdAt)}
                                                    {c.note && <> · {c.note}</>}
                                                </div>
                                                <div className="catalog-lines mt-2">
                                                    {
                                                        c.lines.map((l, i) =>
                                                            <div key={i} className={"catalog-line" + (l.worse ? " is-worse" : "")}>
                                                                <span className="catalog-line-label">{l.label}</span>
                                                                <span className="catalog-line-from">{l.from}</span>
                                                                <Icon icon="arrow-right" type="solid" size={10} />
                                                                <span className="catalog-line-to">{l.to}</span>
                                                            </div>
                                                        )
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })
                            }
                        </div>
            }

        </Drawer>
    )
}
